import { ArrowLeft, ArrowRight, CheckCircle, MapPin, Question, Trophy } from '@phosphor-icons/react';

import { HG_CARTE_FOND } from '@/data/hg-carte-fond';
import { pixelsDepuisPoint, pointDepuisPixels } from '@/lib/hg-carte-projection';
import { cn } from '@/lib/utils';
import {
  CATEGORIE_LABELS,
  NiveauBadge,
  descriptionNiveau,
  libelleProgression,
  type HgCategorieReponseQcm,
  type HgNiveauMaitrise,
} from './hg-college-qcm-vue';

/* ── Types (miroir des types serveur, packages/application/src/trpc/rows/hg-college.ts) ── */

export interface HgCarteItemRow {
  id: string;
  consigne: string;
  support_implicite: boolean;
  critere_id: string;
}

export interface HgCarteExerciceRow {
  id: string;
  fiche_id: string;
  fiche_code: string;
  fiche_titre: string;
  famille: 'carte_croquis';
  position: number;
  enonce: string;
  consigne: string | null;
  duree_indicative_s: number | null;
  items: HgCarteItemRow[];
}

export interface HgCarteRetourItemRow {
  item_id: string;
  notion_code: string | null;
  notion: string | null;
  categorie: HgCategorieReponseQcm;
  je_ne_sais_pas: boolean;
  piege_atteint: string | null;
  explication: string | null;
  zone_attendue: {
    centre: { lon: number; lat: number };
    rayon_km: number;
  };
}

export interface HgCarteRetourCritereRow {
  critere_id: string;
  code: string;
  libelle: string;
  niveau_atteint: HgNiveauMaitrise;
  categorie_dominante: HgCategorieReponseQcm;
  aide_utilisee: boolean;
  n_observations: number;
  dominance_etablie: boolean;
  items: HgCarteRetourItemRow[];
}

export interface HgCarteTentativeResultRow {
  tentative_id: string;
  tentative_n: number;
  niveau_global: HgNiveauMaitrise;
  aide_utilisee: boolean;
  criteres: HgCarteRetourCritereRow[];
}

/* Valeur réservée à l'ignorance assumée, même convention que
   CHOIX_JE_NE_SAIS_PAS côté QCM : la route reçoit `point: null` et
   `jeNeSaisPas: true`, jamais cette chaîne. */
export const JE_NE_SAIS_PAS_CARTE = '__je_ne_sais_pas__' as const;
export type ReponseCarte = { lon: number; lat: number } | typeof JE_NE_SAIS_PAS_CARTE;

/* ── Vue d'un exercice — sans API ni état propre ──────────────────────────────── */

export interface HgCarteExerciceVueProps {
  exercice: HgCarteExerciceRow;
  index: number;
  total: number;
  seriesFaites?: readonly boolean[];
  totalQuestions?: number;
  answers: Record<string, ReponseCarte | undefined>;
  result?: HgCarteTentativeResultRow;
  submitting?: boolean;
  submitError?: string | null;
  onAnswer: (itemId: string, reponse: ReponseCarte) => void;
  onPrecedent: () => void;
  onSuivant: () => void;
  onValider: () => void;
}

export function HgCarteExerciceVue({
  exercice,
  index,
  total,
  seriesFaites,
  totalQuestions,
  answers,
  result,
  submitting = false,
  submitError = null,
  onAnswer,
  onPrecedent,
  onSuivant,
  onValider,
}: HgCarteExerciceVueProps) {
  const allAnswered = exercice.items.length > 0 && exercice.items.every((item) => answers[item.id] !== undefined);

  const annonceRetour = result
    ? `Réponse enregistrée. ${result.criteres
        .map((critere) => `${critere.libelle} : niveau ${critere.niveau_atteint} sur 4`)
        .join('. ')}.`
    : '';

  return (
    <div>
      <p role="status" className="sr-only">{annonceRetour}</p>
      <div className="flex items-start gap-[10px] mb-2">
        <MapPin size={24} weight="light" className="text-primary shrink-0 mt-[2px]" />
        <h2 className="text-[19px] font-bold text-foreground leading-[1.3]">{exercice.fiche_titre}</h2>
      </div>
      {seriesFaites && seriesFaites.length > 1 && (
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex gap-[5px]" aria-hidden="true">
            {seriesFaites.map((faite, rang) => (
              <span
                key={rang}
                className={cn(
                  'h-[6px] w-9 rounded-full',
                  faite ? 'bg-naia-blue' : rang === index ? 'bg-naia-sky' : 'bg-border',
                )}
              />
            ))}
          </div>
          <span className="text-[14px] text-muted-foreground">
            {libelleProgression(seriesFaites)}
          </span>
        </div>
      )}
      <div className="text-[14px] text-muted-foreground mb-6">
        Série {index + 1} / {total} — {exercice.items.length} repère{exercice.items.length > 1 ? 's' : ''} à placer
      </div>

      {exercice.items.length === 0 ? (
        <p className="text-muted-foreground text-sm mb-6">Contenu indisponible pour cette question.</p>
      ) : result ? (
        <CarteRetourExercice result={result} items={exercice.items} answers={answers} />
      ) : (
        <CarteQuestionExercice items={exercice.items} answers={answers} onAnswer={onAnswer} />
      )}

      {submitError && <p role="alert" className="text-destructive text-sm mt-4">{submitError}</p>}

      <div className="flex gap-3 mt-7">
        <button
          type="button"
          disabled={index === 0}
          onClick={onPrecedent}
          className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card border border-border bg-card text-foreground font-bold text-[15px] cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
        >
          <ArrowLeft size={18} />
          Précédent
        </button>

        {!result && exercice.items.length > 0 && (
          <button
            type="button"
            disabled={!allAnswered || submitting}
            onClick={onValider}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <CheckCircle size={18} weight="bold" />
            {submitting ? 'Envoi…' : 'Valider'}
          </button>
        )}

        {result && index < total - 1 && (
          <button
            type="button"
            onClick={onSuivant}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card"
          >
            <ArrowRight size={18} />
            Suivant
          </button>
        )}
      </div>

      {result && index === total - 1 && (
        <section className="mt-7 rounded-card border border-naia-orange-bg bg-card p-5">
          <p className="flex items-center gap-[10px] m-0 text-[22px] font-extrabold text-foreground mb-2">
            <Trophy size={26} weight="fill" className="text-naia-orange shrink-0" />
            Bien joué.
          </p>
          <p className="m-0 text-[15px] text-muted-foreground leading-[1.6]">
            {totalQuestions
              ? `Tu es allé au bout de cette fiche : ${total} séries, ${totalQuestions} repères.`
              : 'Tu es allé au bout de cette fiche.'}{' '}
            Le détail de chaque question reste au-dessus, si tu veux y revenir.
          </p>
        </section>
      )}
    </div>
  );
}

/* ── Sous-composants ─────────────────────────────────────────────────────────── */

function CarteFond({
  children,
  onClick,
  ariaLabel = 'Fond de carte',
  className = 'block w-full h-auto',
}: {
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <svg
      viewBox={`0 0 ${HG_CARTE_FOND.largeur} ${HG_CARTE_FOND.hauteur}`}
      className={className}
      onClick={onClick}
      role="img"
      aria-label={ariaLabel}
    >
      <rect width={HG_CARTE_FOND.largeur} height={HG_CARTE_FOND.hauteur} fill="#dfeaf2" />
      <g fill="#cfdcc6" stroke="#7f9175" strokeWidth={0.7} strokeLinejoin="round">
        {HG_CARTE_FOND.contours.map((d, rang) => (
          <path key={rang} d={d} />
        ))}
      </g>
      {children}
    </svg>
  );
}

export function CarteQuestionExercice({
  items,
  answers,
  onAnswer,
}: {
  items: HgCarteItemRow[];
  answers: Record<string, ReponseCarte | undefined>;
  onAnswer: (itemId: string, reponse: ReponseCarte) => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      {items.map((item) => {
        const valeur = answers[item.id];
        const point = valeur && valeur !== JE_NE_SAIS_PAS_CARTE ? valeur : null;
        const clicPixels = point ? pixelsDepuisPoint(point.lon, point.lat) : null;

        const placer = (event: React.MouseEvent<SVGSVGElement>) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * HG_CARTE_FOND.largeur;
          const y = ((event.clientY - rect.top) / rect.height) * HG_CARTE_FOND.hauteur;
          onAnswer(item.id, pointDepuisPixels(x, y));
        };

        return (
          <div key={item.id}>
            <p className="text-[18px] font-bold text-foreground leading-[1.4] mb-4">{item.consigne}</p>
            <div className="rounded-card border border-border bg-card overflow-hidden">
              <CarteFond
                onClick={placer}
                ariaLabel={`Carte à compléter : ${item.consigne}`}
                className="block w-full h-auto cursor-crosshair"
              >
                {clicPixels && (
                  <circle cx={clicPixels.x} cy={clicPixels.y} r={7} fill="#1f4e79" stroke="#fff" strokeWidth={2} />
                )}
              </CarteFond>
            </div>
            <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
              <p className="text-[13px] text-muted-foreground m-0">
                Fond de carte : Natural Earth, domaine public. Clique pour placer le repère.
              </p>
              <label
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-[7px] rounded-card border border-dashed border-border text-muted-foreground font-[600] text-[13px] cursor-pointer',
                  'transition-colors hover:border-primary hover:text-foreground',
                  valeur === JE_NE_SAIS_PAS_CARTE && 'border-solid border-primary text-foreground bg-accent',
                )}
              >
                <input
                  type="radio"
                  name={`hg-carte-${item.id}`}
                  checked={valeur === JE_NE_SAIS_PAS_CARTE}
                  onChange={() => onAnswer(item.id, JE_NE_SAIS_PAS_CARTE)}
                  className="sr-only"
                />
                <Question size={15} weight="bold" className="shrink-0" />
                Je ne sais pas
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CarteRetourExercice({
  result,
  items,
  answers,
}: {
  result: HgCarteTentativeResultRow;
  items: HgCarteItemRow[];
  answers: Record<string, ReponseCarte | undefined>;
}) {
  const consigneParItemId = new Map(items.map((item) => [item.id, item.consigne]));
  return (
    <div className="flex flex-col gap-4">
      {result.criteres.map((critere) => (
        <div key={critere.critere_id} className="bg-card border border-border rounded-card p-5">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <h2 className="text-[15px] font-bold text-foreground m-0">{critere.libelle}</h2>
            <NiveauBadge niveau={critere.niveau_atteint} />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {descriptionNiveau(critere)}
          </p>

          <div className="flex flex-col gap-6">
            {critere.items.map((itemRetour) => {
              const valeur = answers[itemRetour.item_id];
              const point = valeur && valeur !== JE_NE_SAIS_PAS_CARTE ? valeur : null;
              const clicPixels = point ? pixelsDepuisPoint(point.lon, point.lat) : null;
              const attenduPixels = pixelsDepuisPoint(
                itemRetour.zone_attendue.centre.lon,
                itemRetour.zone_attendue.centre.lat,
              );

              return (
                <div key={itemRetour.item_id} className="border-t border-border pt-4">
                  {itemRetour.notion && (
                    <h3 className="text-[14.5px] font-bold text-foreground mb-[6px] mt-0">{itemRetour.notion}</h3>
                  )}
                  <div className="text-[14px] text-muted-foreground mb-2">
                    {consigneParItemId.get(itemRetour.item_id) ?? ''}
                  </div>
                  <div className="rounded-card border border-border bg-card overflow-hidden mb-2">
                    <CarteFond>
                      <circle
                        cx={attenduPixels.x}
                        cy={attenduPixels.y}
                        r={12}
                        fill="none"
                        stroke="#1f4e79"
                        strokeWidth={2.5}
                        strokeDasharray="5 4"
                      />
                      {clicPixels && (
                        <circle
                          cx={clicPixels.x}
                          cy={clicPixels.y}
                          r={7}
                          fill={itemRetour.categorie === 'notion_mobilisee' ? '#1f4e79' : '#a1531b'}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      )}
                    </CarteFond>
                  </div>
                  <div className="text-[14px] text-muted-foreground mb-1">
                    {itemRetour.je_ne_sais_pas
                      ? 'Tu as répondu « je ne sais pas »'
                      : CATEGORIE_LABELS[itemRetour.categorie]}
                  </div>
                  {itemRetour.piege_atteint && (
                    <p className="text-[15px] text-muted-foreground leading-[1.55] mb-1">
                      Tu as désigné {itemRetour.piege_atteint}.
                    </p>
                  )}
                  {itemRetour.explication && (
                    <p className="text-[15px] text-muted-foreground leading-[1.55]">{itemRetour.explication}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
